// DCA-A6 · Decline-start robustness (frontend).
//
// Spec: docs/domain/dca_tier1_spec.md §4 (递减起点稳健化).
// Picks the decline anchor from a monthly production series so a single-month
// spike (test rate / frac flow-back / metering error) does not become the
// anchor and inflate qi / distort Di. Mirrors the BE algorithm so the
// front-end default and the drag bounds agree with the engine.
//
// Series shape: [[isoDate, rate], ...] (the `history` array returned by the
// DCA endpoints). Rates are monthly volumes / daily rates — only their relative
// magnitude matters for peak selection.

const DEFAULT_RAMP_SKIP = 1; // skip first N months of ramp-up
const DEFAULT_SMOOTH_WINDOW = 3; // centered rolling-median window (months)

function median(values) {
  const arr = values.filter((v) => v != null && !Number.isNaN(v)).slice();
  if (arr.length === 0) return null;
  arr.sort((a, b) => a - b);
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

// Centered rolling median of the rate column.
function rollingMedian(rates, window) {
  const half = Math.floor(window / 2);
  return rates.map((_, i) => {
    const lo = Math.max(0, i - half);
    const hi = Math.min(rates.length - 1, i + half);
    return median(rates.slice(lo, hi + 1));
  });
}

function argmax(values) {
  let best = -Infinity;
  let bestIdx = -1;
  values.forEach((v, i) => {
    if (v != null && v > best) {
      best = v;
      bestIdx = i;
    }
  });
  return bestIdx;
}

/**
 * Robust decline-start picker.
 *
 * @param {Array<[string, number]>} history  monthly [isoDate, rate] series.
 * @param {Object} [opts]
 * @param {number} [opts.rampSkip=1]      months of initial ramp-up to ignore.
 * @param {number} [opts.smoothWindow=3]  centered rolling-median window.
 * @returns {{ index: number, date: string|null, source: string }}
 *   source ∈ { "auto_smoothed", "auto_argmax_fallback" }.
 */
export function pickRobustDeclineStart(history, opts = {}) {
  const rampSkip = opts.rampSkip ?? DEFAULT_RAMP_SKIP;
  const smoothWindow = opts.smoothWindow ?? DEFAULT_SMOOTH_WINDOW;

  if (!Array.isArray(history) || history.length === 0) {
    return { index: -1, date: null, source: "auto_argmax_fallback" };
  }

  const rates = history.map((row) => Number(row[1]));

  // Not enough points to smooth meaningfully → fall back to raw argmax.
  if (history.length <= rampSkip + 1) {
    const idx = argmax(rates);
    return {
      index: idx,
      date: idx >= 0 ? history[idx][0] : null,
      source: "auto_argmax_fallback",
    };
  }

  // 1) Ignore the first `rampSkip` months (ramp-up not yet at peak).
  // 2) Take argmax of the smoothed series so a single spike cannot win.
  const smoothed = rollingMedian(rates, smoothWindow);
  const masked = smoothed.map((v, i) => (i < rampSkip ? null : v));

  let idx = argmax(masked);
  let source = "auto_smoothed";

  // Degenerate smoothing (all masked / flat) → raw argmax fallback.
  if (idx < 0) {
    idx = argmax(rates);
    source = "auto_argmax_fallback";
  }

  return {
    index: idx,
    date: idx >= 0 ? history[idx][0] : null,
    source,
  };
}

// Snap an arbitrary date (e.g. from a drag) to the nearest sample date in the
// history series, returning its index + iso date. Keeps the manual anchor on a
// real production month so the engine re-fits on a valid point.
export function snapToHistory(history, isoDate) {
  if (!Array.isArray(history) || history.length === 0 || !isoDate) {
    return { index: -1, date: null };
  }
  const target = new Date(isoDate).getTime();
  let bestIdx = 0;
  let bestDiff = Infinity;
  history.forEach((row, i) => {
    const diff = Math.abs(new Date(row[0]).getTime() - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  });
  return { index: bestIdx, date: history[bestIdx][0] };
}
